import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import '../../css/card.css';

import PatientTrackingFollowUpDate from './PatientTrackingFollowUpDate';
import PatientTrackingActionsList from './PatientTrackingActionsList';
import AddPatientTrackingActionModal from './AddPatientTrackingActionModal';

import { 
    shouldShowPatientTracking, 
    getCurrentFollowUpDate 
} from '../../utils/patientTrackingUtils';

import { getActionsByPatientResult } from '../../common/services/next-gen/PatientTrackingService';

/**
 * PatientTrackingSection Component
 * 
 * Complete patient tracking section for display within patient results.
 * Shows follow-up date, "Add Follow Up Tracking Action" button, and list of existing actions.
 * 
 * This is a reusable component that can be integrated into any result display.
 * 
 * @component
 * @example
 * <PatientTrackingSection
 *   patientResult={resultObject}
 *   patientResultId="guid-here"
 *   readOnly={false}
 * />
 */
const PatientTrackingSection = ({ patientResult, patientResultId, readOnly, onTrackingActionAdded }) => {
const [trackingActions, setTrackingActions] = useState([]);
const [loadingActions, setLoadingActions] = useState(true);
const [showAddModal, setShowAddModal] = useState(false);
const [currentFollowUpDate, setCurrentFollowUpDate] = useState(null);
const [selectedAction, setSelectedAction] = useState(null);

/**
 * Helper function to get action ID from an action object
 * Handles multiple possible property names from the API
 */
const getActionId = (action) => {
    return action.trackingActionId 
        || action.id 
        || action.patientTrackingActionId 
        || action.actionId;
};

    /**
     * Load tracking actions from API
     */
    const loadTrackingActions = async () => {
        if (!patientResultId || !shouldShowPatientTracking(patientResult)) {
            setLoadingActions(false);
            return;
        }

        try {
            const actions = await getActionsByPatientResult(patientResultId);
            const followUpDate = getCurrentFollowUpDate(patientResult, actions);

            setTrackingActions(actions);
            setCurrentFollowUpDate(followUpDate);
            setLoadingActions(false);
        } catch (error) {
            console.error('Error loading tracking actions:', error);
            setLoadingActions(false);
        }
    };

    // Load tracking actions on mount and when patientResultId changes
    useEffect(() => {
        loadTrackingActions();
    }, [patientResultId]);

    /**
     * Handle when a new action is added or existing action is updated
     */
    const handleActionAdded = (action, isEdit = false) => {
        let updatedActions;
        
        if (isEdit) {
            // Replace the edited action in the list using helper to get IDs
            const updatedActionId = getActionId(action);
            updatedActions = trackingActions.map(a => 
                getActionId(a) === updatedActionId ? action : a
            );
            
            console.log('Edit mode: Replacing action with ID', updatedActionId);
            console.log('Updated actions:', updatedActions);
        } else {
            // Add new action to list
            updatedActions = [action, ...trackingActions];
            console.log('Add mode: Added new action');
        }
        
        // Update current follow-up date
        const updatedFollowUpDate = action.nextFollowUpDate || currentFollowUpDate;

        setTrackingActions(updatedActions);
        setCurrentFollowUpDate(updatedFollowUpDate);
        setShowAddModal(false);
        setSelectedAction(null);

        // Notify parent component if callback provided
        if (onTrackingActionAdded) {
            onTrackingActionAdded(action, isEdit);
        }
    };

    /**
     * Open the add modal
     */
    const openAddModal = () => {
        setSelectedAction(null);
        setShowAddModal(true);
    };

    /**
     * Handle when an action row is clicked (edit mode)
     */
    const handleActionClick = (action) => {
        if (!readOnly) {
            setSelectedAction(action);
            setShowAddModal(true);
        }
    };

    /**
     * Close the add modal
     */
    const closeAddModal = () => {
        setShowAddModal(false);
        setSelectedAction(null);
    };

    // Don't show section if not applicable
    if (!shouldShowPatientTracking(patientResult)) {
        return null;
    }

    return (
        <div className="patient-tracking-section mt-4">
            <Card className="">
                <Card.Header style={{ backgroundColor: 'unset' }}>
                    <span>Patient Tracking</span>
                </Card.Header>
                <Card.Body>
                    {/* Follow-Up Date Display */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <PatientTrackingFollowUpDate
                                followUpDate={currentFollowUpDate}
                                showLabel={true}
                            />
                        </div>
                        <div className="col-md-6 text-right">
                            {!readOnly && (
                                <button
                                    className="btn btn-submit"
                                    onClick={openAddModal}
                                >
                                    <i className="fas fa-plus mr-2"></i>
                                    Add Follow Up Tracking Action
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tracking Actions List */}
                    {loadingActions ? (
                        <div className="text-center py-3">
                            <i className="fas fa-spinner fa-spin"></i> Loading tracking actions...
                        </div>
                    ) : (
                        <PatientTrackingActionsList
                            actions={trackingActions}
                            showHeader={true}
                            onActionClick={!readOnly ? handleActionClick : undefined}
                        />
                    )}

                    {!loadingActions && trackingActions.length === 0 && (
                        <div className="text-center text-muted py-3">
                            <em>No patient tracking actions recorded</em>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Add/Edit Action Modal */}
            <AddPatientTrackingActionModal
                show={showAddModal}
                onHide={closeAddModal}
                patientResultId={patientResultId}
                currentFollowUpDate={currentFollowUpDate}
                onActionAdded={handleActionAdded}
                existingAction={selectedAction}
            />
        </div>
    );
};

export default PatientTrackingSection;
